/**
 * Tone -> class strings.
 *
 * Every class here is a design token from `tailwind.config.js`; no raw Tailwind
 * palette (`slate-*`, `green-*`, `amber-*`, `red-*`, `blue-*`) may appear.
 *
 * Contrast contract (WCAG 2.2, verified numerically — see
 * .omo/plans/layout-hybrid-implementation.md §2.4):
 *   chip text on its own background: ok 5.39 / warn 4.57 / danger 5.75 (light),
 *                                    8.11 / 8.03 / 6.82 (dark)  — all >= 4.5
 *   `text-*` roles on app/surface/surface-2: >= 4.68 light, >= 5.39 dark
 *   `border-strong` reaches 3.02 only over `surface-card` (white) — never use it
 *   over `surface-light`/`surface-2-light` for an interactive outline (SC 1.4.11).
 */
import type { Tone } from '@shared/config/theme';

/** Chip / badge: background + text + border, both themes. */
export const toneChipClasses: Record<Tone, string> = {
  ok: 'bg-state-ok-bg-light text-state-ok-light border-state-ok-light/20 dark:bg-state-ok-bg-dark dark:text-state-ok-dark dark:border-state-ok-dark/20',
  warn: 'bg-state-warn-bg-light text-state-warn-light border-state-warn-light/20 dark:bg-state-warn-bg-dark dark:text-state-warn-dark dark:border-state-warn-dark/20',
  danger:
    'bg-state-danger-bg-light text-state-danger-light border-state-danger-light/20 dark:bg-state-danger-bg-dark dark:text-state-danger-dark dark:border-state-danger-dark/20',
  info: 'bg-state-info-bg-light text-state-info-light border-state-info-light/20 dark:bg-state-info-bg-dark dark:text-state-info-dark dark:border-state-info-dark/20',
  neutral:
    'bg-surface-2-light text-text-muted-light border-surface-border-light dark:bg-surface-2-dark dark:text-text-muted-dark dark:border-surface-border-dark',
  accent:
    'bg-accent-soft-light text-accent-text-light border-accent-text-light/20 dark:bg-accent-soft-dark dark:text-accent-text-dark dark:border-accent-text-dark/20',
};

/** Text-only role, for values and inline indicators. */
export const toneTextClasses: Record<Tone, string> = {
  ok: 'text-state-ok-light dark:text-state-ok-dark',
  warn: 'text-state-warn-light dark:text-state-warn-dark',
  danger: 'text-state-danger-light dark:text-state-danger-dark',
  info: 'text-state-info-light dark:text-state-info-dark',
  neutral: 'text-text-muted-light dark:text-text-muted-dark',
  accent: 'text-accent-text-light dark:text-accent-text-dark',
};

/**
 * Focus ring for interactive elements. `brand-500` over the app surface is
 * 4.29:1 in dark and 4.47:1 in light (>= 3:1 for SC 1.4.11); in dark we switch
 * to `brand-400` (8.29:1) because `brand-600` would drop to 3.05:1.
 */
export const focusRing =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-card-light dark:focus-visible:ring-brand-400 dark:focus-visible:ring-offset-surface-card-dark';

/** Dot / marker fill, for compact indicators (list bullets, status dots). */
export const toneDotClasses: Record<Tone, string> = {
  ok: 'bg-state-ok-light dark:bg-state-ok-dark',
  warn: 'bg-state-warn-light dark:bg-state-warn-dark',
  danger: 'bg-state-danger-light dark:bg-state-danger-dark',
  info: 'bg-state-info-light dark:bg-state-info-dark',
  neutral: 'bg-text-subtle-light dark:bg-text-subtle-dark',
  accent: 'bg-brand-600 dark:bg-brand-400',
};

/** Soft icon container (rounded square behind a status icon). */
export const toneIconBgClasses: Record<Tone, string> = {
  ok: 'bg-state-ok-bg-light text-state-ok-light dark:bg-state-ok-bg-dark dark:text-state-ok-dark',
  warn: 'bg-state-warn-bg-light text-state-warn-light dark:bg-state-warn-bg-dark dark:text-state-warn-dark',
  danger:
    'bg-state-danger-bg-light text-state-danger-light dark:bg-state-danger-bg-dark dark:text-state-danger-dark',
  info: 'bg-state-info-bg-light text-state-info-light dark:bg-state-info-bg-dark dark:text-state-info-dark',
  neutral:
    'bg-surface-2-light text-text-muted-light dark:bg-surface-2-dark dark:text-text-muted-dark',
  accent:
    'bg-accent-soft-light text-accent-text-light dark:bg-accent-soft-dark dark:text-accent-text-dark',
};

/**
 * Verification series colors (charts). Returns a CSS color value, not a class —
 * Recharts needs a real color string.
 */
export const vizColors = {
  cpu: { light: '#4f46e5', dark: '#9aa4fb' },
  ram: { light: '#0e7490', dark: '#67e8f9' },
  disk: { light: '#b45309', dark: '#e2b25c' },
  io: { light: '#10704a', dark: '#4ecfa0' },
} as const;

export type VizKey = keyof typeof vizColors;

/** Resolve a viz series color for the active theme. */
export function vizColor(key: VizKey, theme: 'light' | 'dark'): string {
  return vizColors[key][theme];
}
